import React from 'react';
import { Button } from '../ui/button';
import { Check, Calendar, ArrowRight, Zap, Users, TrendingUp, Sparkles, RefreshCw } from 'lucide-react';
import CalendlyPopup from '../CalendlyPopup';
import { useCalendlyAndVideo } from '../../hooks/useCalendlyAndVideo';

const PriceReveal = ({ recommendation, wizardData }) => {
  const { isCalendlyOpen, openCalendly, closeCalendly } = useCalendlyAndVideo();
  const { package: packageName, tier, pricePerUser, features } = recommendation;
  const { usersPlanned } = wizardData;

  // Calculate estimated users for display
  const getEstimatedUsers = () => {
    if (usersPlanned === '1-99') return { min: 10, mid: 50, max: 99 };
    if (usersPlanned === '100-499') return { min: 100, mid: 250, max: 499 };
    return { min: 500, mid: 750, max: 1000 };
  };

  const users = getEstimatedUsers();

  const calculateMonthly = (userCount) => {
    if (!pricePerUser) return 'Custom';
    return `$${(userCount * pricePerUser).toLocaleString()}`;
  };

  // What every plan delivers
  const universalBenefits = [
    { icon: Zap, text: 'AI-powered training and role-play in one platform' },
    { icon: TrendingUp, text: 'Faster onboarding and skill mastery' },
    { icon: Users, text: 'Consistent enablement across teams' },
    { icon: Sparkles, text: 'Measurable performance improvement' },
    { icon: RefreshCw, text: 'A modern alternative to traditional LMS systems' },
  ];

  return (
    <div className="bg-gray-800/50 border border-gray-700 rounded-2xl p-8">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-block px-4 py-2 bg-green-600/20 border border-green-500/50 rounded-full mb-4">
          <span className="text-green-400 font-semibold">✓ Pricing Unlocked</span>
        </div>
        <h2 className="text-3xl font-bold text-white mb-2">{packageName}</h2>
        <p className="text-gray-400">{recommendation.userRange} users</p>
      </div>

      {/* Pricing Display */}
      {tier === 'enterprise' ? (
        <div className="bg-gradient-to-br from-indigo-900/30 to-purple-900/30 border-2 border-indigo-500/50 rounded-xl p-8 mb-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-2">Custom Enterprise Pricing</h3>
          <p className="text-gray-400 mb-6">
            Let's build a solution that fits your organization's scale and needs.
          </p>
          <Button
            onClick={openCalendly}
            className="bg-indigo-600 hover:bg-indigo-700 px-8 py-3"
            data-testid="enterprise-proposal-btn"
          >
            Request an Enterprise Proposal
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      ) : (
        <div className="bg-gradient-to-br from-cyan-900/30 to-blue-900/30 border-2 border-cyan-500/50 rounded-xl p-8 mb-8">
          <div className="text-center mb-6">
            <div className="text-5xl font-bold text-cyan-400 mb-2">
              ${pricePerUser}
            </div>
            <div className="text-gray-400">per user / month</div>
          </div>

          <div className="border-t border-gray-700 pt-6">
            <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-4">
              Estimated Monthly Cost
            </h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-gray-900/50 rounded-lg">
                <div className="text-sm text-gray-500 mb-1">{users.min} users</div>
                <div className="text-xl font-bold text-white">{calculateMonthly(users.min)}</div>
              </div>
              <div className="text-center p-4 bg-cyan-900/30 border border-cyan-500/30 rounded-lg">
                <div className="text-sm text-cyan-400 mb-1">{users.mid} users</div>
                <div className="text-2xl font-bold text-cyan-400">{calculateMonthly(users.mid)}</div>
              </div>
              <div className="text-center p-4 bg-gray-900/50 rounded-lg">
                <div className="text-sm text-gray-500 mb-1">{users.max} users</div>
                <div className="text-xl font-bold text-white">{calculateMonthly(users.max)}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* What's Included */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-white mb-4">What's included:</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {features.map((feature, index) => (
            <div key={index} className="flex items-start gap-3">
              <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <span className="text-gray-300 text-sm">{feature}</span>
            </div>
          ))}
        </div>
      </div>

      {/* What Every Plan Delivers */}
      <div className="bg-gray-900/50 border border-gray-700 rounded-xl p-6 mb-8">
        <h3 className="text-lg font-semibold text-white mb-4 text-center">
          What Every Plan Delivers
        </h3>
        <div className="space-y-3">
          {universalBenefits.map((benefit, index) => (
            <div key={index} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                <benefit.icon className="w-4 h-4 text-cyan-400" />
              </div>
              <span className="text-gray-300">{benefit.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* CTAs */}
      {tier !== 'enterprise' && (
        <div className="space-y-4">
          <Button
            onClick={openCalendly}
            className="w-full h-14 bg-cyan-600 hover:bg-cyan-700 text-lg font-semibold"
            data-testid="book-demo-btn"
          >
            <Calendar className="w-5 h-5 mr-2" />
            Schedule a Discovery Call
          </Button>
          <Button
            variant="outline"
            onClick={openCalendly}
            className="w-full border-gray-600 text-gray-300 hover:bg-gray-700"
            data-testid="start-pilot-btn"
          >
            Start a Pilot
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}

      {/* FAQ */}
      <div className="mt-12 pt-8 border-t border-gray-700">
        <h3 className="text-xl font-bold text-white mb-6">Common Questions</h3>
        <div className="space-y-6">
          <div>
            <h4 className="font-semibold text-white mb-2">What's included in the per-user price?</h4>
            <p className="text-gray-400 text-sm">
              Every user gets full access to Traain Engine for AI-powered training, Traain Cadence for role-play practice, and the Navigation AI Layer for guidance and insights. No feature gating — everyone gets everything.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-2">Can we start small and scale up?</h4>
            <p className="text-gray-400 text-sm">
              Absolutely. Most teams start with a pilot group and expand. As you grow past 100 users, you'll automatically qualify for Scaling tier pricing at $15/user.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-2">Is there a minimum commitment?</h4>
            <p className="text-gray-400 text-sm">
              We offer flexible terms. Book a demo and we'll discuss what works best for your organization — monthly, annual, or multi-year options available.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-2">What if we have 500+ users?</h4>
            <p className="text-gray-400 text-sm">
              Enterprise customers get volume-based custom pricing, dedicated support, white-labeling options, and strategic partnership benefits. Request a proposal to learn more.
            </p>
          </div>
        </div>
      </div>
      
      <CalendlyPopup isOpen={isCalendlyOpen} onClose={closeCalendly} />
    </div>
  );
};

export default PriceReveal;
